"use client";
import Image from "next/image";
import Link from "next/link";
import ExpandableBlock from "@/components/ContentBlock/variants/ExpandableBlock";
import { ProcessedCourse } from "@/common/course/types";
import { PartialPartner } from "@/common/partner/types";
import { COURSE_RUN_AVAILABILITY_ARCHIVED } from "@/lib/course/utils/constants";
import getCoursePaymentOption from "@/lib/course/utils/getCoursePaymentOption";
import GetSmarterLogo from "./GetSmarterLogo";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import HeroBadges from "./HeroBadges";
import { ExecEdDetailPageTestResult } from "@/monarch/rules/execEdDetailPageTest/types";
import MonarchResolver from "@/monarch/MonarchResolver";
import CountdownBanner from "./CountdownBanner";
import ExecEdPromoHeroClient from "./ExecEdPromoHeroClient";
import useTranslations from "@/i18n/useTranslations";

interface Props {
  course: ProcessedCourse;
}

const CourseInfo = ({ course }: Props) => {
  const t = useTranslations("Common");
  function getOrganizationLogo(partner: PartialPartner) {
    const logo = (
      <Image
        width={150}
        height={75}
        className="max-w-[165px] md:max-w-full"
        src={partner.logoUrl}
        alt={partner.name || t("organizationLogoAlt")}
        key={partner.key}
      />
    );
    if (!course.partnerLogoOverride && course.partnerNameOverride) {
      return (
        <Link
          className="mr-2 mb-0"
          href={`/school/${partner.slug.toLocaleLowerCase()}`}
          key={partner.key}
        >
          {logo}
        </Link>
      );
    }
    return logo;
  }

  const title = `${course.partners[0]?.name}: ${course.productName}`;
  const subheader: string = course.productShortDescription.replace(
    /<[^>]+>/g,
    "",
  );
  const { productStatus } = course.additionalMetadata?.at(0) || {};
  const isArchived = productStatus === COURSE_RUN_AVAILABILITY_ARCHIVED;
  const paymentOption = getCoursePaymentOption(course);
  const courseDisclaimer =
    course.additionalMetadata?.at(0)?.courseTermOverride?.toLowerCase() ||
    "course";

  return (
    <>
      <MonarchResolver<ExecEdDetailPageTestResult>
        experimentKey="useExecEdDetailPageTest"
        variants={{
          control: (
            <>
              <CountdownBanner
                position="top"
                startDate={course.additionalMetadata?.at(0)?.startDate}
              />
              <div className="mb-6 md:mt-8 flex flex-row justify-center md:justify-start w-full">
                {course.partnerLogoOverride ? (
                  <Image
                    width={150}
                    height={75}
                    className="max-w-[165px] md:max-w-full"
                    src={course.partnerLogoOverride}
                    alt={course.partnerNameOverride || t("organizationLogoAlt")}
                  />
                ) : (
                  course.partners &&
                  course.partners.map((partner) => getOrganizationLogo(partner))
                )}
              </div>
              <h1 className="text-3xl font-bold mt-2 mb-3 lg:mb-4 p-0">
                {title}
              </h1>
              <HeroBadges course={course} position="above-subheader" />
              <ExpandableBlock maxHeight={60} heading="" blur centered={false}>
                <p className="" style={{ wordBreak: "keep-all" }}>
                  {subheader}
                </p>
              </ExpandableBlock>
              <HeroBadges course={course} position="below-subheader" />
              <MonarchDataConsumer<ExecEdDetailPageTestResult>
                experimentKey="useExecEdDetailPageTest"
                render={({ data }) => {
                  return (
                    <ExecEdPromoHeroClient
                      certificateFactoid={data?.certificateFactoid}
                      course={course}
                      paymentOption={paymentOption}
                      hasDateColumn
                      isArchivedExternally={isArchived}
                    />
                  );
                }}
              />
              <CountdownBanner
                className="my-8"
                position="bottom"
                startDate={course.additionalMetadata?.at(0)?.startDate}
              />
              <div className="hidden md:block mt-auto">
                <div className="w-full border-b border-primary-200" />
                <GetSmarterLogo courseDisclaimer={courseDisclaimer} />
              </div>
            </>
          ),
        }}
        fallbackVariant="control"
      />
    </>
  );
};

export default CourseInfo;
